// Catálogos que comparten la API, la base y las pantallas. El `valor` es lo que
// se guarda en MySQL (columnas ENUM): si se agrega uno, va también en una
// migración.

export const ROLES = [
  { valor: 'admin', etiqueta: 'Administrador', detalle: 'Ve todos los clientes y administra usuarios y asignaciones.' },
  { valor: 'contador', etiqueta: 'Contador', detalle: 'Ve y edita los clientes que tiene asignados; puede dar de alta clientes.' },
  { valor: 'auxiliar', etiqueta: 'Auxiliar', detalle: 'Ve los clientes que tiene asignados, sin editar sus datos.' },
] as const;
export type Rol = (typeof ROLES)[number]['valor'];

export const ROLES_ASIGNACION = [
  { valor: 'responsable', etiqueta: 'Responsable' },
  { valor: 'colaborador', etiqueta: 'Colaborador' },
] as const;
export type RolAsignacion = (typeof ROLES_ASIGNACION)[number]['valor'];

export const TIPOS_PERSONA = [
  { valor: 'humana', etiqueta: 'Persona humana' },
  { valor: 'juridica', etiqueta: 'Persona jurídica' },
] as const;
export type TipoPersona = (typeof TIPOS_PERSONA)[number]['valor'];

export const CONDICIONES_IVA = [
  { valor: 'responsable_inscripto', etiqueta: 'Responsable Inscripto' },
  { valor: 'monotributo', etiqueta: 'Monotributo' },
  { valor: 'exento', etiqueta: 'IVA Exento' },
  { valor: 'no_alcanzado', etiqueta: 'No alcanzado' },
  { valor: 'consumidor_final', etiqueta: 'Consumidor final' },
] as const;
export type CondicionIva = (typeof CONDICIONES_IVA)[number]['valor'];

export const CATEGORIAS_MONOTRIBUTO = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K'] as const;

export const REGIMENES_IIBB = [
  { valor: 'local', etiqueta: 'Contribuyente local' },
  { valor: 'convenio_multilateral', etiqueta: 'Convenio Multilateral' },
  { valor: 'regimen_simplificado', etiqueta: 'Régimen simplificado' },
  { valor: 'exento', etiqueta: 'Exento' },
  { valor: 'no_inscripto', etiqueta: 'No inscripto' },
] as const;
export type RegimenIibb = (typeof REGIMENES_IIBB)[number]['valor'];

// Códigos de jurisdicción del Convenio Multilateral (los mismos que usa SIFERE).
export const JURISDICCIONES = [
  { codigo: 901, nombre: 'Ciudad Autónoma de Buenos Aires' },
  { codigo: 902, nombre: 'Buenos Aires' },
  { codigo: 903, nombre: 'Catamarca' },
  { codigo: 904, nombre: 'Córdoba' },
  { codigo: 905, nombre: 'Corrientes' },
  { codigo: 906, nombre: 'Chaco' },
  { codigo: 907, nombre: 'Chubut' },
  { codigo: 908, nombre: 'Entre Ríos' },
  { codigo: 909, nombre: 'Formosa' },
  { codigo: 910, nombre: 'Jujuy' },
  { codigo: 911, nombre: 'La Pampa' },
  { codigo: 912, nombre: 'La Rioja' },
  { codigo: 913, nombre: 'Mendoza' },
  { codigo: 914, nombre: 'Misiones' },
  { codigo: 915, nombre: 'Neuquén' },
  { codigo: 916, nombre: 'Río Negro' },
  { codigo: 917, nombre: 'Salta' },
  { codigo: 918, nombre: 'San Juan' },
  { codigo: 919, nombre: 'San Luis' },
  { codigo: 920, nombre: 'Santa Cruz' },
  { codigo: 921, nombre: 'Santa Fe' },
  { codigo: 922, nombre: 'Santiago del Estero' },
  { codigo: 923, nombre: 'Tierra del Fuego' },
  { codigo: 924, nombre: 'Tucumán' },
] as const;
export const CODIGOS_JURISDICCION = JURISDICCIONES.map((j) => j.codigo);
export function nombreJurisdiccion(codigo: number | null | undefined): string {
  return JURISDICCIONES.find((j) => j.codigo === codigo)?.nombre ?? '';
}

// Servicios de ARCA que el cliente le delega al estudio (Administrador de
// Relaciones). Son los que usa el robot para bajar datos con clave fiscal; el
// `valor` es el identificador del servicio en ARCA. Para sumar uno, verificar
// su identificador en el Administrador de Relaciones.
export const SERVICIOS_ARCA = [
  { valor: 'mcmp', etiqueta: 'Mis Comprobantes' },
  { valor: 'mis_retenciones', etiqueta: 'Mis Retenciones' },
  { valor: 'siap_web_iva', etiqueta: 'Portal IVA' },
  { valor: 'cuenta_corriente_contrib', etiqueta: 'Sistema de Cuentas Tributarias' },
  { valor: 'misfacilidades', etiqueta: 'Mis Facilidades' },
  { valor: 'comarb_sifereweb_consultas', etiqueta: 'SIFERE WEB - Consultas (Convenio Multilateral)' },
  { valor: 'e-ventanilla', etiqueta: 'Domicilio Fiscal Electrónico' },
] as const;
export type ServicioArca = (typeof SERVICIOS_ARCA)[number]['valor'];

export const ESTADOS_DELEGACION = [
  { valor: 'pendiente', etiqueta: 'Pendiente' },
  { valor: 'activa', etiqueta: 'Activa' },
  { valor: 'revocada', etiqueta: 'Revocada' },
] as const;
export type EstadoDelegacion = (typeof ESTADOS_DELEGACION)[number]['valor'];

// Trabajos que hace el robot de ARCA (apps/worker). Cada uno necesita que el
// cliente le haya delegado al estudio los `servicios` indicados, con estado
// activa. `periodo` dice cómo se parte el pedido:
//   mensual    → un trabajo por mes (Mis Comprobantes: los rangos largos vuelven incompletos)
//   trimestral → un trabajo por trimestre calendario (los agentes informan con atraso)
//   rango      → un solo trabajo para todo el rango de meses
//   ninguno    → sin período (trae todo lo vigente)
export const TIPOS_TRABAJO_ARCA = [
  { valor: 'mis_comprobantes_recibidos', etiqueta: 'Mis Comprobantes — recibidos', servicios: ['mcmp'], periodo: 'mensual' },
  { valor: 'mis_comprobantes_emitidos', etiqueta: 'Mis Comprobantes — emitidos', servicios: ['mcmp'], periodo: 'mensual' },
  { valor: 'mis_retenciones', etiqueta: 'Mis Retenciones', servicios: ['mis_retenciones'], periodo: 'trimestral' },
  // Por trimestre: 24 meses en un solo trabajo no entran en el tope de 20 minutos del robot.
  { valor: 'ddjj_iva', etiqueta: 'DDJJ de IVA presentadas', servicios: ['siap_web_iva'], periodo: 'trimestral' },
  { valor: 'mis_facilidades', etiqueta: 'Mis Facilidades (planes de pago)', servicios: ['misfacilidades'], periodo: 'ninguno' },
  // Una foto del día: deudas, DDJJ no presentadas, vencimientos y, de los últimos 24 meses,
  // las DDJJ presentadas y los pagos de todos los impuestos.
  { valor: 'cuenta_tributaria', etiqueta: 'Cuenta tributaria (deudas, DDJJ y pagos)', servicios: ['cuenta_corriente_contrib'], periodo: 'ninguno' },
  // Retenciones y percepciones de IIBB de los agentes (SIRCAR, SIRCREB, SIRPEI) y DDJJ del Convenio, 24 meses.
  { valor: 'sifere', etiqueta: 'SIFERE (Convenio Multilateral)', servicios: ['comarb_sifereweb_consultas'], periodo: 'ninguno' },
  // Solo la lista de comunicaciones: el robot nunca abre una (abrirla la da por notificada).
  { valor: 'dfe', etiqueta: 'Domicilio Fiscal Electrónico', servicios: ['e-ventanilla'], periodo: 'ninguno' },
] as const satisfies readonly { valor: string; etiqueta: string; servicios: readonly ServicioArca[]; periodo: string }[];
export type TipoTrabajoArca = (typeof TIPOS_TRABAJO_ARCA)[number]['valor'];

/** Las DDJJ de IVA hasta este período son F.2002 y se leen en Cuentas Tributarias. */
export const ULTIMO_PERIODO_F2002 = '2025-05';

export const ESTADOS_TRABAJO_ARCA = [
  { valor: 'pendiente', etiqueta: 'En cola' },
  { valor: 'tomado', etiqueta: 'En curso' },
  { valor: 'terminado', etiqueta: 'Terminado' },
  { valor: 'error', etiqueta: 'Con error' },
] as const;
export type EstadoTrabajoArca = (typeof ESTADOS_TRABAJO_ARCA)[number]['valor'];

export const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
] as const;

type ConValor = readonly { valor: string }[];
export function valores<T extends ConValor>(lista: T): [T[number]['valor'], ...T[number]['valor'][]] {
  return lista.map((x) => x.valor) as [T[number]['valor'], ...T[number]['valor'][]];
}
export function etiqueta(lista: readonly { valor: string; etiqueta: string }[], valor: string | null | undefined): string {
  return lista.find((x) => x.valor === valor)?.etiqueta ?? (valor ?? '');
}
